import { useEffect, useRef, useState } from "react";
import {
    Backdrop,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
    Box,
    Button,
    Paper,
    Stack,
    FormControl,
    MenuItem,
    Select,
    InputLabel

} from "@mui/material";
import { changePlan, deleteApiKey, generateApiKey, getApiKey } from "../../services/api";
import { styled } from "@mui/system";

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    backgroundColor: "#f9f9f9",
}));

const KeyBox = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      p: 2,
      bgcolor: (theme) => theme.palette.grey[100],
      borderRadius: 1,
      overflowX: "auto"
    }}
  >
    <Box sx={{ flex: 1, fontFamily: "monospace" }}>{children}</Box>
    <Button
      size="small"
      variant="contained"
      onClick={() => navigator.clipboard.writeText(children)}
      sx={{ textTransform: "none", whiteSpace: "nowrap" }}
    >
      Copy
    </Button>
  </Box>
);

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    fontSize: "1.5rem",
    marginBottom: theme.spacing(2),
    color: "#213757",
}));

const APIKey = () => {
    const [apiKey, setApiKey] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openDomainInput, setOpenDomainInput] = useState(false);
    const [authorizedDomains, setAuthorizedDomains] = useState([]);
    const [showWarning, setShowWarning] = useState(false);
    const [isDeleteAction, setIsDeleteAction] = useState(false);
    const [plan, setPlan] = useState(apiKey?.keyDetail?.plan || "Free");

    const handleChange = (event) => {
        setPlan(event.target.value);
    };

    const domainRef = useRef();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await getApiKey();
                setApiKey(response.data);
            } catch (error) {
                console.error("Error fetching API key:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const generateKey = async () => {
        try {
            setLoading(true);
            const response = await generateApiKey();
            setApiKey(response.data);
        } catch (error) {
            console.error("Error generating API key:", error);
        } finally {
            setLoading(false);
            setShowWarning(false);
        }
    };

    const removeApiKey = async () => {
        try {
            await deleteApiKey();
            setApiKey(null);
        } catch (error) {
            console.error("Error deleting API key:", error);
        } finally {
            setShowWarning(false);
        }
    };

    const handleDelete = () => {
        setIsDeleteAction(true);
        setShowWarning(true);
    };

    const handleRegenerate = () => {
        setIsDeleteAction(false);
        setShowWarning(true);
    };

    const handleCancelWarning = () => {
        setShowWarning(false);
    };

    const handleAddDomain = () => {
        const domain = domainRef.current.value.trim();
        if (!domain) return;
        setAuthorizedDomains((prev) => [...prev, domain]);
        domainRef.current.value = "";
        setOpenDomainInput(false);
    };

    const handleUpgradePlan = async () => {
        let resp = await changePlan(plan)
        setApiKey(resp?.data)
    }

    return (
        <Box maxWidth="700px" mx="auto" mt={4}>
            <SectionTitle variant="h4">API Keys</SectionTitle>

            <StyledPaper elevation={1}>

                {apiKey?.keyDetail ? (
                    <>
                        <Typography variant="subtitle1">API Key</Typography>
                        <KeyBox>{apiKey?.keyDetail?.key}</KeyBox>

                        <Typography variant="subtitle1" sx={{ mt: 2 }}>
                            Encryption Key
                        </Typography>
                        <KeyBox>{apiKey?.keyDetail?.encryptionKey}</KeyBox>

                        <Stack direction="row" spacing={2} mt={3}>
                            <Button variant="outlined" color="error" onClick={handleDelete}>
                                Delete Key
                            </Button>
                            {/* <Button variant="contained" color="primary" onClick={handleRegenerate}>
                                Regenerate Key
                            </Button> */}
                        </Stack>
                    </>
                ) : (
                    <>
                        <Typography>You don’t have an API key.</Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={generateKey}
                            sx={{ mt: 2 }}
                        >
                            Generate Key
                        </Button>
                    </>
                )}
            </StyledPaper>
            {apiKey?.keyDetail ?  <Box sx={{ p: 3, borderRadius: 2, boxShadow: 2, width: 350 }}>
                <Typography variant="h6" sx={{ mt: 3 }}>
                    How to use?
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, wordBreak: "break-word" }}>
                    BASE_URL?api_key=[YOUR_API_KEY]
                </Typography>

                <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                    Existing Plans: {apiKey?.keyDetail?.features?.join(", ") || "N/A"}
                </Typography>

                {/* <Typography variant="h6" gutterBottom>
                    Plans
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="plan-select-label">Select Plan</InputLabel>
                    <Select
                        labelId="plan-select-label"
                        value={plan}
                        label="Select Plan"
                        onChange={handleChange}
                    >
                        <MenuItem value="Free">Free</MenuItem>
                        <MenuItem value="Basic">Basic</MenuItem>
                        <MenuItem value="Premium">Premium</MenuItem>
                        <MenuItem value="Business">Business</MenuItem>
                    </Select>
                </FormControl>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleUpgradePlan(plan)}
                    fullWidth
                >
                    Add / Remove Plan
                </Button> */}
            </Box>: <Typography variant="h6" sx={{ mt: 3 }}>
                    How to use?
                </Typography>}



            {/* <StyledPaper elevation={1}>
                <SectionTitle variant="h6">Authorized Domains</SectionTitle>

                {authorizedDomains.length > 0 ? (
                    authorizedDomains.map((domain) => (
                        <Typography key={domain} variant="body2">
                            • {domain}
                        </Typography>
                    ))
                ) : (
                    <Typography variant="body2">No domains added.</Typography>
                )}

                {openDomainInput ? (
                    <Stack direction="row" spacing={1} mt={2}>
                        <TextField
                            inputRef={domainRef}
                            variant="outlined"
                            size="small"
                            placeholder="Enter domain"
                            fullWidth
                        />
                        <Button variant="contained" onClick={handleAddDomain}>
                            Add
                        </Button>
                    </Stack>
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setOpenDomainInput(true)}
                        sx={{ mt: 2 }}
                    >
                        Add Domain
                    </Button>
                )}
            </StyledPaper> */}

            {/* Loader */}
            <Backdrop open={loading} sx={{ zIndex: 1200 }}>
                <CircularProgress color="inherit" />
            </Backdrop>

            {/* Warning Dialog */}
            <Dialog open={showWarning} onClose={handleCancelWarning}>
                <DialogTitle>Are you sure?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This action will {isDeleteAction ? "delete" : "regenerate"} your API key.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelWarning}>Cancel</Button>
                    <Button
                        onClick={isDeleteAction ? removeApiKey : generateKey}
                        color={isDeleteAction ? "error" : "primary"}
                        variant="contained"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default APIKey;
